import { usePilotage } from '../context';
import { DataTable, OkMark, Panel, SectionState, Unavailable } from '../ui';
import { DailyChart, Legend, TimeChart } from '../charts';
import { dec, ms, num, pct } from '../format';
import { apiDetail } from '../details';

/** Écran 5 — API : trafic réel par service, sondes, erreurs, lenteurs */
export function ApisScreen() {
  const { payload, palette: p, openDetail } = usePilotage();
  const a = payload.sections.apis?.data;
  if (!a) return <Unavailable section={payload.sections.apis} what="API" />;

  const color = (k: string) => (k === 'v1' ? p.v1 : k === 'data' ? p.third : k === 'adm' ? p.fourth : p.v2);
  const rpmDefs = [
    { metric: 'api.v2-api.rpm', label: 'API V2', color: p.v2 },
    { metric: 'api.v1.rpm', label: 'V1', color: p.v1 },
  ];
  const errDefs = [
    { metric: 'api.v2-api.err_pct', label: 'API V2', color: p.v2 },
    { metric: 'api.v1.err_pct', label: 'V1', color: p.v1 },
  ];

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-3 xl:grid-cols-12 xl:grid-rows-[auto_minmax(0,1fr)_minmax(0,1fr)]">
      <Panel title="Services" subtitle="5 dernières minutes, trafic réel + sonde synthétique" className="xl:col-span-12" right={<SectionState section={payload.sections.apis} />}>
        {a.lokiError && <p className="mb-2 text-xs text-red-600">Loki : {a.lokiError}</p>}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {a.upstreams.map((u) => {
            const probes = a.probes.filter((x) => (u.key === 'v2-front' ? x.key === 'v2-front' : u.key === 'v2-api' ? x.key === 'v2-api' : u.key === 'v1' ? x.key === 'v1-app' : u.key === 'data' ? x.key === 'data-api' : x.key === 'adm'));
            return (
              <button
                key={u.key}
                type="button"
                onClick={() => openDetail(apiDetail(payload, p, u.key))}
                className="space-y-1 rounded-lg border border-slate-200 p-3 text-left hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-600"
                style={{ boxShadow: `inset 3px 0 0 ${color(u.key)}` }}
              >
                <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">{u.label}</div>
                <div className="grid grid-cols-3 gap-1 text-xs">
                  <div>
                    <div className="text-slate-400">req/min</div>
                    <div className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">{dec(u.rpm, 0)}</div>
                  </div>
                  <div>
                    <div className="text-slate-400">p95</div>
                    <div className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">{ms(u.p95Ms)}</div>
                  </div>
                  <div>
                    <div className="text-slate-400">5xx</div>
                    <div className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">{pct(u.errorRatePct, 2)}</div>
                  </div>
                </div>
                {probes.map((pr) => (
                  <div key={pr.key} className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">sonde</span>
                    <span className="flex items-center gap-2">
                      <OkMark ok={pr.ok} label={pr.status ? `HTTP ${pr.status}` : 'KO'} />
                      <span className="tabular-nums text-slate-500">{ms(pr.latencyMs)}</span>
                    </span>
                  </div>
                ))}
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel title="Requêtes / min (24 h)" className="xl:col-span-4" right={<Legend defs={rpmDefs} />}>
        <TimeChart defs={rpmDefs} height="100%" />
      </Panel>
      <Panel title="Taux d'erreur 5xx (24 h)" className="xl:col-span-4" right={<Legend defs={errDefs} />}>
        <TimeChart defs={errDefs} format={(n) => `${dec(n, 2)} %`} height="100%" />
      </Panel>
      <Panel
        title="Temps de réponse des sondes (24 h)"
        className="xl:col-span-4"
        right={
          <Legend
            defs={[
              { metric: 'probe.v2-api.ms', label: 'API V2', color: p.v2 },
              { metric: 'probe.v1-app.ms', label: 'V1', color: p.v1 },
              { metric: 'probe.data-api.ms', label: 'API data', color: p.third },
            ]}
          />
        }
      >
        <TimeChart
          defs={[
            { metric: 'probe.v2-api.ms', label: 'API V2', color: p.v2 },
            { metric: 'probe.v1-app.ms', label: 'V1', color: p.v1 },
            { metric: 'probe.data-api.ms', label: 'API data', color: p.third },
          ]}
          format={ms}
          height="100%"
        />
      </Panel>

      <Panel title="Routes en erreur 5xx" subtitle="dernière heure" className="xl:col-span-5" bodyClassName="overflow-y-auto">
        <DataTable
          rows={a.topErrors}
          max={10}
          empty="Aucune erreur 5xx sur la dernière heure"
          columns={[
            { key: 's', label: 'Service', render: (r) => r.service },
            { key: 'r', label: 'Route', render: (r) => <code className="text-xs">{r.method} {r.route}</code> },
            { key: 'c', label: 'Code', align: 'right', render: (r) => r.status },
            { key: 'n', label: 'Nb', align: 'right', render: (r) => num(r.count) },
          ]}
        />
      </Panel>
      <Panel title="p95 par jour" className="xl:col-span-4" right={<Legend defs={[{ metric: 'api.v2-api.p95_ms_day', label: 'API V2', color: p.v2 }, { metric: 'api.v1.p95_ms_day', label: 'V1', color: p.v1 }]} />}>
        <DailyChart
          defs={[
            { metric: 'api.v2-api.p95_ms_day', label: 'API V2', color: p.v2 },
            { metric: 'api.v1.p95_ms_day', label: 'V1', color: p.v1 },
          ]}
          format={ms}
          height="100%"
        />
      </Panel>
      <Panel title="Erreurs applicatives" subtitle="lignes ERROR / CRITICAL dans les logs" className="xl:col-span-3">
        <DataTable
          rows={a.appErrors}
          columns={[
            { key: 'l', label: 'Conteneur', render: (r) => r.label },
            { key: '5', label: '5 min', align: 'right', render: (r) => num(r.count5m) },
            { key: 'h', label: '1 h', align: 'right', render: (r) => num(r.count1h) },
          ]}
        />
        {a.dataInternal && (
          <p className="mt-2 text-xs text-slate-400">
            API data, appels internes : {num(a.dataInternal.requests5m)} en 5 min, {pct(a.dataInternal.errorRatePct, 2)} en erreur
          </p>
        )}
      </Panel>
    </div>
  );
}
